import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { apiErrorMessage, useCreateAccount } from "@/lib/api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateAccountDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const createAccount = useCreateAccount();
  const [accountName, setAccountName] = useState("");
  const [segment, setSegment] = useState("");
  const [region, setRegion] = useState("");
  const [error, setError] = useState("");

  const resetFields = () => {
    setAccountName("");
    setSegment("");
    setRegion("");
    setError("");
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetFields();
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const account = await createAccount.mutateAsync({
        accountName: accountName.trim(),
        segment: segment.trim() || undefined,
        region: region.trim() || undefined,
      });
      resetFields();
      onOpenChange(false);
      if (account) {
        void navigate(`/admin/accounts/${account.account_id}`);
      }
    } catch (err) {
      setError(apiErrorMessage(err, "Could not create the account."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New customer account</DialogTitle>
          <DialogDescription>
            Create a customer account. You can add contacts and grant MCP access on the account page
            once it exists.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="accountName" className="text-foreground text-sm font-medium">
              Account name
            </label>
            <Input
              id="accountName"
              placeholder="Acme Surgical Group"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label htmlFor="accountSegment" className="text-foreground text-sm font-medium">
                Segment <span className="text-muted font-normal">(optional)</span>
              </label>
              <Input
                id="accountSegment"
                placeholder="Hospital"
                value={segment}
                onChange={(e) => setSegment(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="accountRegion" className="text-foreground text-sm font-medium">
                Region <span className="text-muted font-normal">(optional)</span>
              </label>
              <Input
                id="accountRegion"
                placeholder="EMEA"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              />
            </div>
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createAccount.isPending || !accountName.trim()}>
              {createAccount.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Create account"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
